import {isArray} from 'lodash';
import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ErrorWithData} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AsyncData} from '../../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {AsyncLoader} from '../../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ConversationSummaryCommentUnfurlViewer} from './conversationSummaryCommentUnfurlViewer';
import {StyledEmptyDiv, StyledLoaderDiv} from './conversationSummaryCommonStyles';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';

/*
 * Props.
 */

interface ConversationSummaryLinkedConversationsInheritableProps {
  panelId: ViewerPanelId;
  linkedOrRecentlyUnlinkedConversations: AsyncData<ReadonlyArray<ActivityModel>>;
  onLinkedConversationsRetryClick: () => void;
  onRequestClose: () => void;
  isCompact: boolean;
  sectionType?: ConversationSummarySectionsEnum;
}

interface ConversationSummaryLinkedConversationsWithOtherProps
  extends ConversationSummaryLinkedConversationsInheritableProps {
  linkStatus: LinkedConversationStatusesEnum.LINKED;

  otherLinkedConversations: AsyncData<ReadonlyArray<ConversationModel>>;
  onOtherLinkedConversationsRetryClick: () => void;
}

interface ConversationSummaryLinkedConversationsWithNoOtherProps
  extends ConversationSummaryLinkedConversationsInheritableProps {
  linkStatus: LinkedConversationStatusesEnum.ORIGINAL | LinkedConversationStatusesEnum.UNLINKED;

  otherLinkedConversations?: never;
  onOtherLinkedConversationsRetryClick?: never;
}

type ConversationSummaryLinkedConversationsProps =
  | ConversationSummaryLinkedConversationsWithNoOtherProps
  | ConversationSummaryLinkedConversationsWithOtherProps;

/*
 * Style.
 */

const StyledLinkedConversationsDiv = styled.div`
  display: grid;
`;

const StyledNoLinkedConversationsDiv = styled(StyledEmptyDiv)<{$isCompact: boolean}>`
  font-size: ${(p) => (p.$isCompact ? fontSizes.small : fontSizes.medium)};
`;

const StyledLinkedConversationsContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledLinkedConversationDiv = styled.div`
  padding: 4px 0px;
`;

const StyledSubheaderTitle = styled.div<{$isCompact: boolean}>`
  margin-top: ${(p) => (p.$isCompact ? '10px' : '18px')};
  margin-bottom: 4px;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${(p) => (p.$isCompact ? fontSizes.small : fontSizes.medium)};
  font-weight: ${fontWeights.semibold};

  display: flex;
  gap: 7px;
  align-items: center;
`;

const StyledCounterDiv = styled.div`
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 16px;
  background: ${(p) => p.theme.greys.shade30};
  border-radius: 8px;
  height: 16px;
  min-width: 16px;
  text-align: center;
  padding: 0 4px;
`;

/*
 * Component.
 */

export const ConversationSummaryLinkedConversations: FC<ConversationSummaryLinkedConversationsProps> = memo(
  (props) => (
    <StyledLinkedConversationsDiv data-testid={props.sectionType}>
      {renderLinkedConversationsContent(props)}
    </StyledLinkedConversationsDiv>
  ),
);

function renderLinkedConversationsContent(props: ConversationSummaryLinkedConversationsProps) {
  const {linkedOrRecentlyUnlinkedConversations, onLinkedConversationsRetryClick} = props;

  if (linkedOrRecentlyUnlinkedConversations.status !== AsyncStatusesEnum.LOADED) {
    return renderLoader(linkedOrRecentlyUnlinkedConversations.status, onLinkedConversationsRetryClick);
  }

  if (
    !isArray(linkedOrRecentlyUnlinkedConversations.data) ||
    linkedOrRecentlyUnlinkedConversations.data.length === 0
  ) {
    return (
      <StyledNoLinkedConversationsDiv
        data-testid="conversationSummaryNoLinkedConversations"
        $isCompact={props.isCompact}
      >
        {renderNoLinkedConversationsMessage(false)}
      </StyledNoLinkedConversationsDiv>
    );
  }

  const {panelId} = props;
  return (
    <>
      {renderLinkedConversations(
        linkedOrRecentlyUnlinkedConversations.data,
        panelId,
        props.onRequestClose,
        props.isCompact,
      )}
      {props.linkStatus === LinkedConversationStatusesEnum.LINKED &&
        maybeRenderOtherLinkedConversations(props)}
    </>
  );
}

function renderLoader(
  linkedConversationsLoadingStatus: AsyncStatusesEnum,
  onLinkedConversationsRetryClick: () => void,
) {
  return (
    <StyledLoaderDiv>
      <AsyncLoader
        onRetryClick={onLinkedConversationsRetryClick}
        size={VisualSizesEnum.SMALL}
        status={linkedConversationsLoadingStatus}
      />
    </StyledLoaderDiv>
  );
}

function renderNoLinkedConversationsMessage(isOther: boolean) {
  if (isOther) {
    return <Trans id="conversation-summary-linked-conversations-none-other">No conversations</Trans>;
  }
  return <Trans id="conversation-summary-linked-conversations-none">No linked conversations</Trans>;
}

function renderLinkedConversations(
  linkedOrRecentlyUnlinkedConversationActivities: ReadonlyArray<ActivityModel>,
  panelId: ViewerPanelId,
  onRequestClose: () => void,
  isCompact?: boolean,
) {
  return (
    <StyledLinkedConversationsContainerDiv>
      {linkedOrRecentlyUnlinkedConversationActivities.map((activity) => (
        <StyledLinkedConversationDiv key={activity.id}>
          {renderLinkedConversation(
            activity,
            panelId,
            onRequestClose,
            linkedOrRecentlyUnlinkedConversationActivities.length,
            isCompact,
          )}
        </StyledLinkedConversationDiv>
      ))}
    </StyledLinkedConversationsContainerDiv>
  );
}

function maybeRenderOtherLinkedConversations(props: ConversationSummaryLinkedConversationsProps) {
  const {otherLinkedConversations, onOtherLinkedConversationsRetryClick} = props;
  if (!otherLinkedConversations) {
    return null;
  }
  const renderOtherLinkedConversationsContent = () => {
    if (otherLinkedConversations.status !== AsyncStatusesEnum.LOADED) {
      return renderLoader(otherLinkedConversations.status, onOtherLinkedConversationsRetryClick);
    }
    if (otherLinkedConversations.data.length === 0) {
      return (
        <StyledNoLinkedConversationsDiv
          data-testid="conversationSummaryNoOtherLinkedConversations"
          $isCompact={props.isCompact}
        >
          {renderNoLinkedConversationsMessage(true)}
        </StyledNoLinkedConversationsDiv>
      );
    }

    return (
      <StyledLinkedConversationsContainerDiv>
        {otherLinkedConversations.data.map((conversation) => (
          <StyledLinkedConversationDiv key={conversation.id}>
            <ConversationSummaryCommentUnfurlViewer
              conversation={conversation}
              panelId={props.panelId}
              onRequestClose={props.onRequestClose}
              isCompact={props.isCompact}
            />
          </StyledLinkedConversationDiv>
        ))}
      </StyledLinkedConversationsContainerDiv>
    );
  };

  const renderOtherLinkedConversationsTitleContent = () => {
    if (props.isCompact && otherLinkedConversations.status === AsyncStatusesEnum.LOADED) {
      return (
        <Plural
          id="hover-linked-conversation-title-other-x"
          value={otherLinkedConversations.data.length}
          one="1 other linked conversation"
          other="# other linked conversations"
        />
      );
    }
    return (
      <>
        <Trans id="conversation-summary-linked-conversations-other">Other linked conversations</Trans>{' '}
        {otherLinkedConversations.status === AsyncStatusesEnum.LOADED &&
          otherLinkedConversations.data.length > 0 && (
            <StyledCounterDiv>{otherLinkedConversations.data.length}</StyledCounterDiv>
          )}
      </>
    );
  };

  return (
    <StyledLinkedConversationsContainerDiv>
      <StyledSubheaderTitle $isCompact={props.isCompact}>
        {renderOtherLinkedConversationsTitleContent()}
      </StyledSubheaderTitle>
      {renderOtherLinkedConversationsContent()}
    </StyledLinkedConversationsContainerDiv>
  );
}

function renderLinkedConversation(
  linkedConversationActivity: ActivityModel,
  panelId: ViewerPanelId,
  onRequestClose: () => void,
  numLinkedConversations: number,
  isCompact?: boolean,
) {
  switch (linkedConversationActivity.visualType) {
    case ActivityVisualTypesEnum.DETAILED:
      return (
        <ConversationSummaryCommentUnfurlViewer
          activity={linkedConversationActivity}
          panelId={panelId}
          onRequestClose={onRequestClose}
          numLinkedConversations={numLinkedConversations}
          isCompact={isCompact}
        />
      );

    default:
      reportToBugsnag(
        new ErrorWithData('Tried to render linked conversation of an unsupported type', {
          activity: linkedConversationActivity,
        }),
      );
      return null;
  }
}
